import { Box, Fade, Grid } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import RoundBackgroundInput from '../input/RoundBackgroundInput';
import PasswordStrengthView from '../input/PasswordStrengthView';
import { images } from '../../assets/images';
import { ContinueRef } from '../../onboarding/types';
import SignUpConsents from './SignUpConsents';
import { useSignUpForm } from './hooks/useSignUpForm';

const SignUpForm = forwardRef<ContinueRef, unknown>((_props, ref) => {
  const form = useSignUpForm();

  useImperativeHandle(ref, () => ({
    continuePressed: form.submit,
  }));

  return (
    <Fade in={true}>
      <Grid
        container
        className={form.mobile ? 'px-[16px]' : 'w-full'}
        rowGap={2}
        alignContent={form.mobile ? 'center' : undefined}
        sx={form.mobile ? { flex: 1, marginTop: '100px' } : {}}
      >
        {/* Access Code */}
        <Grid
          container
          size={{ xs: 12 }}
          direction="row"
          justifyContent="center"
        >
          <Grid size={{ md: 6, xs: 12 }} alignSelf="center">
            <RoundBackgroundInput
              placeholder={form.t('accessCode')}
              onChange={(e) => {
                form.setCouponError('');
                form.setCoupon(e.target.value);
              }}
              helperText={form.couponError}
              error={form.couponError.length > 0}
              value={form.coupon}
            />
          </Grid>
        </Grid>

        {/* First & Last Name */}
        <Grid
          container
          size={{ xs: 12 }}
          direction={form.isJa ? 'row-reverse' : undefined}
          sx={form.isJa ? { flexDirection: 'row-reverse' } : undefined}
        >
          <Grid flex={1}>
            <RoundBackgroundInput
              placeholder={form.t('firstName')}
              onChange={(e) => {
                form.setNameError('');
                form.setName(e.target.value);
              }}
              onBlur={() => {
                if (form.name.length === 0) {
                  form.setNameError(form.t('pleaseCompleteThisField'));
                }
              }}
              error={form.nameError.length > 0}
              helperText={form.nameError}
              value={form.name}
            />
          </Grid>

          <Box
            component="img"
            src={images.form.nameInput.focused}
            className="mx-4"
          />

          <Grid flex={1}>
            <RoundBackgroundInput
              placeholder={form.t('lastName')}
              onChange={(e) => {
                form.setLastNameError('');
                form.setLastName(e.target.value);
              }}
              onBlur={() => {
                if (form.lastName.length === 0) {
                  form.setLastNameError(form.t('pleaseCompleteThisField'));
                }
              }}
              helperText={form.lastNameError}
              error={form.lastNameError.length > 0}
              value={form.lastName}
            />
          </Grid>
        </Grid>

        {/* Email */}
        <Grid
          container
          size={{ xs: 12 }}
          direction="row"
          justifyContent="center"
        >
          <RoundBackgroundInput
            onChange={(e) => {
              form.setEmailError('');
              form.setEmail(e.target.value);
            }}
            onBlur={form.handleBlurEmail}
            error={form.emailError.length > 0}
            helperText={form.emailError}
            placeholder={form.t('email')}
            inputMode="email"
            value={form.email}
          />
        </Grid>

        {/* Password */}
        <Grid
          container
          size={{ xs: 12 }}
          direction="column"
          justifyContent="center"
        >
          <RoundBackgroundInput
            placeholder={form.t('password')}
            type={form.passwordOn ? 'password' : undefined}
            onChange={(e) => form.setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <Box
                  component="img"
                  src={
                    form.passwordOn
                      ? images.v2.inputs.passwordOn
                      : images.v2.inputs.passwordOff
                  }
                  className="hover:cursor-pointer"
                  onClick={() => form.setPasswordOn((prev) => !prev)}
                />
              ),
            }}
            value={form.password}
          />
          <PasswordStrengthView password={form.password} />
        </Grid>

        {/* Consents / Terms */}
        <SignUpConsents
          isSyd={form.isSyd}
          isJa={form.isJa}
          termsURI={form.termsURI}
          healthDataConsent={form.healthDataConsent}
          setHealthDataConsent={form.setHealthDataConsent}
          researchConsent={form.researchConsent}
          setResearchConsent={form.setResearchConsent}
          tcsAgreed={form.tcsAgreed}
          setTcsAgreed={form.setTcsAgreed}
        />
      </Grid>
    </Fade>
  );
});

export default SignUpForm;
